// Hook to manage URL query state with multi-value facets, typed enum singles,
// and free-form single keys.

import { useCallback, useMemo } from 'react';
import type { NavigateFunction } from 'react-router';

export type RouteQueryPrimitive = string | number;

export type RouteQueryFacetDefinition<TValue extends RouteQueryPrimitive> = {
  key: string;
  // Convert a raw URL value into a typed app value. Return undefined to drop
  // unknown/stale URLs instead of leaking arbitrary strings into UI state.
  parse: (raw: string) => TValue | undefined;
  // Convert a typed app value back into its URL representation. This is how
  // display IDs can stay rich while URLs use stable slugs.
  serialize?: (value: TValue) => string;
  // Serialized code-point order is the deterministic default. Numeric facets
  // can provide a numeric comparator so 2 sorts before 10.
  compare?: (left: TValue, right: TValue) => number;
};

export type RouteQueryFacetDefinitions<
  TState extends Record<string, RouteQueryPrimitive[]>,
> = {
  [K in keyof TState]: RouteQueryFacetDefinition<TState[K][number]>;
};

type EnumDefinition<TValue extends string | null> = {
  key: string;
  values: readonly Exclude<TValue, null>[];
  // Optional when simple inclusion in `values` is enough. Use this for aliases
  // and backwards-compatible URL migrations, e.g. section=moderation -> player-access.
  parse?: (raw: string) => Exclude<TValue, null> | undefined;
  serialize?: (value: Exclude<TValue, null>) => string;
} & (null extends TValue
  ? {
      optional: true;
      default?: Exclude<TValue, null>;
    }
  : {
      optional?: false;
      default: Exclude<TValue, null>;
    });

type EnumDefinitions<TState extends Record<string, string | null>> = {
  [K in keyof TState]: EnumDefinition<TState[K]>;
};

export type RouteQueryScalarDefinition<
  TValue extends RouteQueryPrimitive | null,
> = {
  key: string;
  parse: (raw: string) => Exclude<TValue, null> | undefined;
  serialize?: (value: Exclude<TValue, null>) => string;
  equals?: (
    left: Exclude<TValue, null>,
    right: Exclude<TValue, null>
  ) => boolean;
} & (null extends TValue
  ? {
      optional: true;
      default?: Exclude<TValue, null>;
    }
  : {
      optional?: false;
      default: Exclude<TValue, null>;
    });

export type RouteQueryScalarDefinitions<
  TState extends Record<string, RouteQueryPrimitive | null>,
> = {
  [K in keyof TState]: RouteQueryScalarDefinition<TState[K]>;
};

type UpdateRouteOptions<
  TEnumState extends Record<string, string | null>,
  TScalarState extends Record<string, RouteQueryPrimitive | null>,
> = {
  singles?: Record<string, string | null>;
  enums?: Partial<TEnumState>;
  scalars?: Partial<TScalarState>;
  replace?: boolean;
};

type UseRouteQueryStateArgs<
  TFacetState extends Record<string, RouteQueryPrimitive[]>,
  TEnumState extends Record<string, string | null>,
  TScalarState extends Record<string, RouteQueryPrimitive | null>,
> = {
  pathname: string;
  searchParams: URLSearchParams;
  navigate: NavigateFunction;
  facets: RouteQueryFacetDefinitions<TFacetState>;
  enums?: EnumDefinitions<TEnumState>;
  scalars?: RouteQueryScalarDefinitions<TScalarState>;
  singleKeys?: string[];
};

const dedupe = <TValue extends RouteQueryPrimitive>(
  values: TValue[]
): TValue[] => Array.from(new Set(values));

const codePointCompare = (left: string, right: string): number => {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
};

export const parseRouteQueryFacetValues = <TValue extends RouteQueryPrimitive>(
  rawValues: readonly string[],
  facet: RouteQueryFacetDefinition<TValue>
): TValue[] =>
  dedupe(
    rawValues
      .map((value) => facet.parse(value))
      .filter((value): value is TValue => value !== undefined)
  );

export const normalizeRouteQueryFacetValues = <
  TValue extends RouteQueryPrimitive,
>(
  values: TValue[],
  facet: RouteQueryFacetDefinition<TValue>
): TValue[] => {
  const serialize = facet.serialize ?? String;
  return dedupe(values)
    .slice()
    .sort(
      facet.compare ??
        ((left, right) => codePointCompare(serialize(left), serialize(right)))
    );
};

export const useRouteQueryState = <
  TFacetState extends Record<string, RouteQueryPrimitive[]>,
  TEnumState extends Record<string, string | null> = Record<string, never>,
  TScalarState extends Record<string, RouteQueryPrimitive | null> = Record<
    string,
    never
  >,
>({
  pathname,
  searchParams,
  navigate,
  facets,
  enums,
  scalars,
  singleKeys = [],
}: UseRouteQueryStateArgs<TFacetState, TEnumState, TScalarState>) => {
  // `useSearchParams` is the low-level React Router primitive. This hook sits
  // above it: callers still provide the current URLSearchParams, but receive
  // parsed, typed state plus update helpers that preserve the route contract.
  const selected = useMemo<TFacetState>(
    () =>
      (Object.keys(facets) as Array<keyof TFacetState>).reduce(
        (acc, stateKey) => {
          const facet = facets[stateKey];
          // Facets are repeatable query params: ?game=a&game=b. They are used
          // for filter sets where the empty array means "no filter".
          const rawValues = searchParams.getAll(facet.key);
          acc[stateKey] = parseRouteQueryFacetValues(
            rawValues,
            facet
          ) as TFacetState[typeof stateKey];
          return acc;
        },
        {} as TFacetState
      ),
    [facets, searchParams]
  );

  const values = useMemo<TEnumState>(() => {
    if (!enums) {
      return {} as TEnumState;
    }
    return (Object.keys(enums) as Array<keyof TEnumState>).reduce(
      (acc, key) => {
        const def = enums[key];
        // Enums are single-value params: ?sort=name or ?dialog=login. Optional
        // enums parse missing/invalid values as null; required enums fall back
        // to their configured default without forcing that default into the URL.
        const raw = searchParams.get(def.key);
        const parsed =
          raw !== null
            ? (def.parse?.(raw) ??
              (((def.values as readonly string[]).includes(raw)
                ? raw
                : undefined) as
                Exclude<TEnumState[typeof key], null> | undefined))
            : undefined;
        acc[key] = (parsed ??
          (def.optional ? null : def.default)) as TEnumState[typeof key];
        return acc;
      },
      {} as TEnumState
    );
  }, [enums, searchParams]);

  const scalarValues = useMemo<TScalarState>(() => {
    if (!scalars) {
      return {} as TScalarState;
    }
    return (Object.keys(scalars) as Array<keyof TScalarState>).reduce(
      (acc, key) => {
        const def = scalars[key];
        const raw = searchParams.get(def.key);
        const parsed = raw === null ? undefined : def.parse(raw);
        acc[key] = (parsed ??
          (def.optional ? null : def.default)) as TScalarState[typeof key];
        return acc;
      },
      {} as TScalarState
    );
  }, [scalars, searchParams]);

  const singleValues = useMemo(
    () =>
      singleKeys.reduce<Record<string, string | null>>((acc, key) => {
        acc[key] = searchParams.get(key);
        return acc;
      }, {}),
    [searchParams, singleKeys]
  );

  const buildSearch = useCallback(
    (
      nextFacetState: TFacetState,
      options?: {
        singles?: Record<string, string | null>;
        enums?: Partial<TEnumState>;
        scalars?: Partial<TScalarState>;
      }
    ) => {
      const params = new URLSearchParams();

      // Rebuild from typed state instead of mutating the existing search params.
      // That drops stale unknown query values and keeps URL output deterministic.
      for (const stateKey of Object.keys(facets) as Array<keyof TFacetState>) {
        const facet = facets[stateKey];
        const serialize = facet.serialize ?? String;
        const facetValues = normalizeRouteQueryFacetValues(
          nextFacetState[stateKey],
          facet
        );
        facetValues.forEach((value) => {
          params.append(facet.key, serialize(value));
        });
      }

      if (enums) {
        for (const enumKey of Object.keys(enums) as Array<keyof TEnumState>) {
          const def = enums[enumKey];
          // Use key presence rather than `??` so callers can pass `null` to
          // explicitly clear an optional enum (e.g. closing a dialog).
          const overrides = options?.enums;
          const nextValue =
            overrides && (enumKey as string) in overrides
              ? overrides[enumKey]
              : values[enumKey];
          // Required enum defaults are omitted from the URL so canonical routes
          // stay short. Optional enum values are written only when non-null.
          if (nextValue && (def.optional || nextValue !== def.default)) {
            const enumValue = nextValue as Exclude<
              TEnumState[typeof enumKey],
              null
            >;
            params.set(
              def.key,
              def.serialize ? def.serialize(enumValue) : enumValue
            );
          }
        }
      }

      if (scalars) {
        for (const scalarKey of Object.keys(scalars) as Array<
          keyof TScalarState
        >) {
          const def = scalars[scalarKey];
          const overrides = options?.scalars;
          const nextValue =
            overrides && (scalarKey as string) in overrides
              ? overrides[scalarKey]
              : scalarValues[scalarKey];
          if (nextValue === null || nextValue === undefined) {
            continue;
          }
          const scalarValue = nextValue as Exclude<
            TScalarState[typeof scalarKey],
            null
          >;
          const isDefault =
            !def.optional &&
            (def.equals
              ? def.equals(scalarValue, def.default)
              : Object.is(scalarValue, def.default));
          if (!isDefault) {
            params.set(
              def.key,
              def.serialize ? def.serialize(scalarValue) : String(scalarValue)
            );
          }
        }
      }

      singleKeys.forEach((singleKey) => {
        // Singles are intentionally unparsed escape hatches for leaf UI state
        // like ?song=... where the caller owns validation elsewhere.
        const singleOverrides = options?.singles;
        const nextValue =
          singleOverrides && singleKey in singleOverrides
            ? singleOverrides[singleKey]
            : singleValues[singleKey];
        if (nextValue) {
          params.set(singleKey, nextValue);
        }
      });

      const query = params.toString();
      return query ? `?${query}` : '';
    },
    [facets, enums, scalarValues, scalars, singleKeys, singleValues, values]
  );

  const updateRoute = useCallback(
    (
      nextFacetState: TFacetState,
      options?: UpdateRouteOptions<TEnumState, TScalarState>
    ) => {
      const nextSearch = buildSearch(nextFacetState, {
        singles: options?.singles,
        enums: options?.enums,
        scalars: options?.scalars,
      });
      const currentSearch = searchParams.toString();
      if (nextSearch.replace(/^\?/, '') === currentSearch) {
        return;
      }
      // We use navigate rather than setSearchParams so callers can also change
      // pathname-specific query state without coupling this hook to one router
      // primitive. `replace` is passed through for transient UI state.
      navigate(`${pathname}${nextSearch}`, { replace: options?.replace });
    },
    [buildSearch, navigate, pathname, searchParams]
  );

  const updateEnums = useCallback(
    (nextEnums: Partial<TEnumState>, options?: { replace?: boolean }) => {
      updateRoute(selected, { enums: nextEnums, replace: options?.replace });
    },
    [selected, updateRoute]
  );

  const updateScalars = useCallback(
    (nextScalars: Partial<TScalarState>, options?: { replace?: boolean }) => {
      updateRoute(selected, {
        scalars: nextScalars,
        replace: options?.replace,
      });
    },
    [selected, updateRoute]
  );

  return {
    selected,
    values,
    scalarValues,
    singleValues,
    buildSearch,
    updateRoute,
    updateEnums,
    updateScalars,
  };
};
